// Wave director: The Tower-style pacing — quiet intermissions, budgeted
// assault waves, mop-up windows, and a boss every `waves.boss_every` waves.
// Spawns go through budget-driven formations with spawn-safety telegraphs.
// Conforms to GDD §8 (simplified for Gate 1) and §8.11.

import type { WorldState } from './state.js';
import type { Enemy, Vec2, EnemyDef } from '../types.js';
import { cfg } from '../config.js';
import { wrapArena, wrappedDelta } from './collision.js';
import { enemyHpAtTime } from './combat.js';
import { RngService } from './rng.js';
import { EventBus } from './events.js';
import enemiesJson from '../content/enemies.json';

const ENEMY_DEFS = (enemiesJson as { version: string; entries: EnemyDef[] }).entries;
const DRIFTFIN_ID = 'enemy.driftfin';
/** Subspace bruiser: fields the wormhole bonus level's formations (sim/subspace.ts). */
export const VOID_FIEND_ID = 'enemy.void-fiend';
/** Boss-wave enemy: one spawned at the start of every boss-wave assault. */
export const ABYSS_MAW_ID = 'enemy.abyss-maw';
/** Second boss def: boss waves alternate abyss-maw / void-razor (spec-arsenal). */
export const VOID_RAZOR_ID = 'enemy.void-razor';
/** Flat boss HP multiplier: fights must outlast a full attack cycle. */
const BOSS_HP_MULT = 3.2;
/** Trash-budget multiplier on boss waves (the boss itself is free). */
const BOSS_WAVE_BUDGET_MULT = 0.6;

/**
 * Wave-gated squad composition (spec-arsenal): each formation is a
 * homogeneous squad of one archetype, picked per formation by weighted roll
 * on the `spawn` stream. Weights activate from `fromWave` onward.
 */
const COMPOSITION: readonly { id: string; weight: number; fromWave: number }[] = [
  { id: DRIFTFIN_ID, weight: 100, fromWave: 1 },
  { id: 'enemy.dartling', weight: 40, fromWave: 3 },
  { id: 'enemy.bulwark', weight: 25, fromWave: 4 },
  { id: 'enemy.spitter', weight: 20, fromWave: 5 },
  { id: 'enemy.weaver', weight: 30, fromWave: 6 },
  { id: 'enemy.dodger', weight: 25, fromWave: 7 },
  { id: 'enemy.splitter', weight: 22, fromWave: 8 },
  { id: 'enemy.leech', weight: 22, fromWave: 5 },
  { id: 'enemy.carrier', weight: 18, fromWave: 6 }
];

/** Elite roll: from wave 4, per-unit chance min(0.18, 0.06 + 0.01·wave). */
function eliteChanceForWave(wave: number): number {
  if (wave < 4) return 0;
  return Math.min(0.18, 0.06 + 0.01 * wave);
}

// Gate 1 simplified: aggressive baseline so the swarm reaches the 200-cap for visual review.
const BASELINE_THREAT_PER_MINUTE = 1500;
// Camera-view radius used as the in-view proxy for spawn telegraph decisions.
const CAMERA_VIEW_RADIUS = 40;
// Arena margin kept inside hard walls for spawn placement.
const ARENA_MARGIN = 2;
// Stream spawn spacing between consecutive units.
const STREAM_SPACING = 2;
// Minimum separation for dispersed formation placement.
const DISPERSED_MIN_SEP = 4;
// Grace telegraph for every spawn (~0.2 s): enemies scale in over this window
// and stay frozen/untargetable even when they spawn outside the camera view.
const SPAWN_GRACE_TICKS = 12;

export type WavePhase = 'intermission' | 'assault' | 'mop_up';

interface WaveState {
  /** Assault counter; 0 before the first assault starts. */
  wave: number;
  phase: WavePhase;
  /** Ticks remaining in the current phase. */
  ticksLeft: number;
  /** Total ticks of the current phase (for HUD progress). */
  phaseTicks: number;
  /** False until the first assault starts (shorter first intermission). */
  started: boolean;
  /** Threat released evenly over the assault window; budgetLeft drains to 0. */
  budgetTotal: number;
  budgetLeft: number;
  budgetPerTick: number;
}

interface DirectorState {
  threat: number;
  seed: number;
  wave: WaveState;
  /** Committed squad archetype waiting for budget (null = roll next spawn). */
  pendingDef: EnemyDef | null;
}

const directorState = new WeakMap<WorldState, DirectorState>();

export function findEnemyDef(id: string): EnemyDef {
  const def = ENEMY_DEFS.find((d) => d.id === id);
  if (!def) {
    throw new Error(`Enemy definition ${id} not found in enemies.json`);
  }
  return def;
}

function secondsToTicks(path: string): number {
  return Math.max(1, Math.round(cfg.getNumber(path) * cfg.getNumber('sim.step_rate_hz')));
}

function freshWaveState(): WaveState {
  const first = secondsToTicks('waves.first_intermission_s');
  return {
    wave: 0,
    phase: 'intermission',
    ticksLeft: first,
    phaseTicks: first,
    started: false,
    budgetTotal: 0,
    budgetLeft: 0,
    budgetPerTick: 0,
  };
}

function getDirectorState(world: WorldState): DirectorState {
  let state = directorState.get(world);
  // A new run (seed change) restarts the wave cycle and the threat bank.
  if (!state || state.seed !== world.runSeed) {
    state = { threat: 0, seed: world.runSeed, wave: freshWaveState(), pendingDef: null };
    directorState.set(world, state);
  }
  return state;
}

// Toroidal distance: shortest path across the seam. Used for camera-view
// checks, spawn-safety radius, and intra-formation spacing.
function dist(a: Vec2, b: Vec2): number {
  const d = wrappedDelta(a, b);
  return Math.sqrt(d.x * d.x + d.y * d.y);
}

function vec2(x = 0, y = 0): Vec2 {
  return { x, y };
}

function pickFormationType(rng: RngService): 'ring' | 'stream' | 'dispersed' {
  return rng.get('spawn').pick(['ring', 'stream', 'dispersed'] as const);
}

function randomRange(rng: RngService, min: number, max: number): number {
  return rng.get('spawn').next_range(min, max);
}

function randomInt(rng: RngService, minInclusive: number, maxExclusive: number): number {
  return minInclusive + rng.get('spawn').next_int(maxExclusive - minInclusive);
}

function isInCameraView(pos: Vec2, playerPos: Vec2): boolean {
  return dist(pos, playerPos) < CAMERA_VIEW_RADIUS;
}

function generateRingFormation(
  world: WorldState,
  rng: RngService
): { positions: Vec2[]; goal: Vec2 }[] {
  const playerPos = world.player.pos;
  const radius = randomRange(rng, 22, 28);
  const count = randomInt(rng, 5, 9);
  const baseAngle = randomRange(rng, 0, Math.PI * 2);
  const step = (Math.PI * 2) / count;
  const positions: Vec2[] = [];
  for (let i = 0; i < count; i++) {
    const angle = baseAngle + i * step;
    const pos = {
      x: playerPos.x + Math.cos(angle) * radius,
      y: playerPos.y + Math.sin(angle) * radius,
    };
    wrapArena(pos);
    positions.push(pos);
  }
  // Ring units converge on the player.
  return positions.map((pos) => ({ positions: [pos], goal: playerPos }));
}

function generateStreamFormation(
  world: WorldState,
  rng: RngService
): { positions: Vec2[]; goal: Vec2 }[] {
  const halfArena = cfg.getNumber('sim.arena_size') / 2;
  // Swarm Surge custom event: while eventStreamEdge is set, streams enter
  // from the event's seeded edge instead of rolling one (consumes no draw).
  const edge = world.eventStreamEdge ?? rng.get('spawn').next_int(4); // 0: top, 1: right, 2: bottom, 3: left
  const count = randomInt(rng, 5, 9);
  const cross = randomRange(rng, -halfArena + ARENA_MARGIN, halfArena - ARENA_MARGIN);
  const positions: Vec2[] = [];
  let goal = vec2();

  switch (edge) {
    case 0: // top, flowing down
      for (let i = 0; i < count; i++) {
        positions.push({ x: cross, y: halfArena - i * STREAM_SPACING });
      }
      goal = { x: cross, y: playerTargetY(world) };
      break;
    case 1: // right, flowing left
      for (let i = 0; i < count; i++) {
        positions.push({ x: halfArena - i * STREAM_SPACING, y: cross });
      }
      goal = { x: playerTargetX(world), y: cross };
      break;
    case 2: // bottom, flowing up
      for (let i = 0; i < count; i++) {
        positions.push({ x: cross, y: -halfArena + i * STREAM_SPACING });
      }
      goal = { x: cross, y: playerTargetY(world) };
      break;
    default: // left, flowing right
      for (let i = 0; i < count; i++) {
        positions.push({ x: -halfArena + i * STREAM_SPACING, y: cross });
      }
      goal = { x: playerTargetX(world), y: cross };
      break;
  }

  for (const pos of positions) {
    wrapArena(pos);
  }
  return positions.map((pos) => ({ positions: [pos], goal }));
}

function playerTargetX(world: WorldState): number {
  // No walls to stay clear of on a torus: stream goals aim at the player.
  return world.player.pos.x;
}

function playerTargetY(world: WorldState): number {
  return world.player.pos.y;
}

function generateDispersedFormation(
  world: WorldState,
  rng: RngService
): { positions: Vec2[]; goal: Vec2 }[] {
  const playerPos = world.player.pos;
  const count = randomInt(rng, 4, 8);
  const placed: Vec2[] = [];
  const maxAttempts = 10;

  for (let i = 0; i < count; i++) {
    let pos: Vec2 | null = null;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const angle = randomRange(rng, 0, Math.PI * 2);
      const radius = randomRange(rng, 14, 40);
      const candidate = {
        x: playerPos.x + Math.cos(angle) * radius,
        y: playerPos.y + Math.sin(angle) * radius,
      };
      wrapArena(candidate);
      const tooClose = placed.some((p) => dist(p, candidate) < DISPERSED_MIN_SEP);
      if (!tooClose) {
        pos = candidate;
        break;
      }
    }
    if (pos) placed.push(pos);
  }

  // Dispersed units converge on the player.
  return placed.map((pos) => ({ positions: [pos], goal: playerPos }));
}

function generateFormation(
  world: WorldState,
  rng: RngService
): { positions: Vec2[]; goal: Vec2 }[] {
  const type = pickFormationType(rng);
  switch (type) {
    case 'ring':
      return generateRingFormation(world, rng);
    case 'stream':
      return generateStreamFormation(world, rng);
    case 'dispersed':
    default:
      return generateDispersedFormation(world, rng);
  }
}

export function initEnemy(
  e: Enemy,
  def: EnemyDef,
  pos: Vec2,
  goal: Vec2,
  runTime: number,
  tick: number,
  telegraphUntil: number
): void {
  e.def_id = def.id;
  e.pos = { x: pos.x, y: pos.y };
  e.vel = { x: 0, y: 0 };
  e.hp = enemyHpAtTime(def.hp, runTime);
  e.max_hp = e.hp;
  e.hitbox_radius = def.hitbox_radius;
  e.threat_score = def.threat_score;
  e.steering.weights = [...def.steering_weights];
  e.steering.formation_slot = 0;
  e.steering.goal = { x: goal.x, y: goal.y };
  e.age_ticks = 0;
  e.telegraph_until = telegraphUntil;
  e.targetable = telegraphUntil <= tick;
  e.alive = true;
  e.spawn_tick = tick;
  e.elite = false;
  e.affixes = [];
  // Clear any boss marker left over from a previous life in this pool slot.
  e.boss = undefined;
  // Same for a subspace formation drive (sim/subspace.ts) left by a previous
  // tenant — a fresh spawn always starts on its def's own steering.
  e.formation_drive = undefined;
  // Virtual depth plane: deterministic spread across 3 layers so the swarm
  // reads as a 3D formation; enemies can shift planes when crowded.
  e.plane = (e.pool_index % 3) as 0 | 1 | 2;
}

/**
 * Wave-driven director (The Tower-style pacing): the run alternates quiet
 * intermissions (zero spawns — time to collect motes, heal, reposition) with
 * budgeted assaults spent through the existing formation machinery, followed
 * by a mop-up window that ends early once the field is clear. Every
 * `waves.boss_every`-th assault also spawns a single boss. While the wormhole
 * subspace trip runs (any non-idle bonus phase), the director is fully inert:
 * spawning and the wave clock both suspend, and the wave resumes exactly
 * where it paused — the subspace formation director (sim/subspace.ts) owns
 * the field while the normal plane is detached (state.ts pool swap). Fully
 * deterministic: phase transitions derive from the sim tick and the shared
 * 'spawn' RNG stream.
 */
export function updateDirector(world: WorldState, dt: number, tick: number): void {
  if (dt <= 0) return;

  const state = getDirectorState(world);
  if (world.bonus.phase !== 'idle') return;

  stepWaveClock(world, state, tick);
  // Spawns happen only during the assault window; intermission and mop-up
  // leave the field to the player (quiet time / mop-up). The archetype is
  // rolled per formation from the wave-gated composition table.
  if (state.wave.phase === 'assault') {
    spendThreat(world, state, tick, null);
  }
}

function stepWaveClock(world: WorldState, state: DirectorState, tick: number): void {
  const w = state.wave;

  // Release the wave budget evenly across the assault window. The Swarm
  // Surge event multiplier (?? 1 normally) raises spawn pressure without
  // draining the wave's own budget any faster — the surplus is the event's.
  if (w.phase === 'assault') {
    const release = Math.min(w.budgetPerTick, w.budgetLeft);
    const pressure = world.eventThreatMult ?? 1;
    state.threat += release * pressure;
    w.budgetLeft -= release;
    const maxBank = w.budgetTotal * pressure;
    if (state.threat > maxBank) state.threat = maxBank;
  }

  // Mop-up ends the moment the field is clear.
  if (w.phase === 'mop_up' && world.enemies.aliveCount === 0) {
    world.events.emit('wave_clear', { wave: w.wave }, tick);
    enterIntermission(w);
    return;
  }

  w.ticksLeft--;
  if (w.ticksLeft > 0) return;

  switch (w.phase) {
    case 'intermission':
      startAssault(world, state, tick);
      break;
    case 'assault':
      w.phase = 'mop_up';
      w.phaseTicks = secondsToTicks('waves.mop_up_s');
      w.ticksLeft = w.phaseTicks;
      break;
    case 'mop_up':
      // Timeout: stragglers carry over into the next wave.
      enterIntermission(w);
      break;
  }
}

function enterIntermission(w: WaveState): void {
  w.phase = 'intermission';
  // Quiet windows shrink as waves progress: full calm early, short breathers
  // once the run heats up (floor of 4 s so a wave never skips its pause).
  const seconds = w.started
    ? Math.max(4, cfg.getNumber('waves.intermission_s') - 0.3 * w.wave)
    : cfg.getNumber('waves.first_intermission_s');
  w.phaseTicks = Math.max(1, Math.round(seconds * cfg.getNumber('sim.step_rate_hz')));
  w.ticksLeft = w.phaseTicks;
}

function startAssault(world: WorldState, state: DirectorState, tick: number): void {
  const w = state.wave;
  w.wave++;
  w.started = true;
  w.phase = 'assault';
  w.phaseTicks = secondsToTicks('waves.assault_s');
  w.ticksLeft = w.phaseTicks;

  const bossEvery = Math.max(1, Math.round(cfg.getNumber('waves.boss_every')));
  const bossWave = w.wave % bossEvery === 0;
  const budget = cfg.getNumber('waves.base_budget') + cfg.getNumber('waves.budget_per_wave') * w.wave;
  w.budgetTotal = bossWave ? budget * BOSS_WAVE_BUDGET_MULT : budget;
  w.budgetLeft = w.budgetTotal;
  w.budgetPerTick = w.budgetTotal / w.phaseTicks;

  // Vanguard: front-load one formation's worth of threat so the assault's
  // first group arrives immediately instead of trickling in mid-wave.
  const vanguard = Math.min(w.budgetLeft, 32);
  state.threat += vanguard;
  w.budgetLeft -= vanguard;

  world.events.emit('wave_start', { wave: w.wave, boss: bossWave }, tick);
  if (bossWave) spawnWaveBoss(world, w.wave, tick);
}

/** Telegraph one boss at the edge of the camera view; it hunts the player. */
function spawnWaveBoss(world: WorldState, wave: number, tick: number): void {
  // Boss waves alternate defs (spec-arsenal): boss-wave index 1 → abyss-maw,
  // 2 → void-razor, 3 → abyss-maw, … Both carry the boss flag + elite.
  const bossEvery = Math.max(1, Math.round(cfg.getNumber('waves.boss_every')));
  const bossIndex = Math.max(1, Math.round(wave / bossEvery));
  const def = findEnemyDef(bossIndex % 2 === 1 ? ABYSS_MAW_ID : VOID_RAZOR_ID);
  const angle = world.rng.get('spawn').next_range(0, Math.PI * 2);
  const pos = {
    x: world.player.pos.x + Math.cos(angle) * 34,
    y: world.player.pos.y + Math.sin(angle) * 34,
  };
  wrapArena(pos);
  const e = world.enemies.acquire();
  if (!e) {
    world.events.emit('pool_exhausted', { kind: 'enemy' }, tick);
    return;
  }
  const stepRate = cfg.getNumber('sim.step_rate_hz');
  const telegraphUntil = tick + Math.max(1, Math.ceil(cfg.getNumber('spawn.in_view_telegraph') * stepRate));
  initEnemy(e, def, pos, world.player.pos, world.runTime, tick, telegraphUntil);
  e.elite = true;
  // Bosses must not fold: flat HP multiplier on top of run-time scaling so
  // the fight lasts long enough for its attack cycles to matter.
  e.hp = Math.round(e.hp * BOSS_HP_MULT);
  e.max_hp = e.hp;
  // Boss cycle (boss-ai.ts) starts when the spawn telegraph materializes.
  e.boss = { phase_index: 0, enrage_at_tick: tick + 3600, phase_tick: telegraphUntil };
  world.events.emit(
    'spawn',
    { kind: 'enemy', def_id: def.id, pos: { x: pos.x, y: pos.y }, telegraph_until: telegraphUntil },
    tick
  );
  world.events.emit('boss_spawn', { enemy_id: e.id, def_id: def.id, wave, pos: { x: pos.x, y: pos.y } }, tick);
}

/** Wave state snapshot for the HUD (label + phase progress bar). */
export interface WaveInfo {
  /** Wave shown to the player: the incoming wave during intermission. */
  displayWave: number;
  phase: WavePhase;
  /** 0..1 progress through the current phase. */
  progress01: number;
  /** True when the displayed wave is a boss wave. */
  boss: boolean;
  /** Bravery bonus awarded if the player calls the wave early right now
   * (0 when not callable — assault, mop-up, or the bonus fight). */
  callBonus: number;
}

/** Bravery bonus tuning: skipped seconds × (base + per-wave × (wave − 1)). */
const BRAVERY_BASE_PER_SECOND = 10;
const BRAVERY_PER_WAVE = 5;

/** Score awarded for calling a wave early: scales with skipped quiet time
 * and the incoming wave (later waves are deadlier, so the bribe grows). */
export function braveryBonus(incomingWave: number, skippedTicks: number): number {
  if (skippedTicks <= 0) return 0;
  const seconds = skippedTicks / cfg.getNumber('sim.step_rate_hz');
  return Math.round(seconds * (BRAVERY_BASE_PER_SECOND + BRAVERY_PER_WAVE * Math.max(0, incomingWave - 1)));
}

/**
 * Player command: skip the rest of the intermission and start the assault
 * now. Returns the skipped ticks (0 when not callable — assault/mop-up in
 * progress or the wormhole bonus fight has the wave clock paused).
 */
export function callWaveEarly(world: WorldState, tick: number): number {
  const state = getDirectorState(world);
  const w = state.wave;
  if (w.phase !== 'intermission' || world.bonus.phase === 'fighting') return 0;
  const skipped = w.ticksLeft;
  startAssault(world, state, tick);
  return skipped;
}

export function getWaveInfo(world: WorldState): WaveInfo {
  const w = getDirectorState(world).wave;
  const displayWave = w.phase === 'intermission' ? w.wave + 1 : Math.max(1, w.wave);
  const bossEvery = Math.max(1, Math.round(cfg.getNumber('waves.boss_every')));
  const callable = w.phase === 'intermission' && world.bonus.phase !== 'fighting';
  return {
    displayWave,
    phase: w.phase,
    progress01: w.phaseTicks > 0 ? Math.min(1, Math.max(0, 1 - w.ticksLeft / w.phaseTicks)) : 1,
    boss: displayWave % bossEvery === 0,
    callBonus: callable ? braveryBonus(displayWave, w.ticksLeft) : 0,
  };
}

/** Roll one squad archetype for this wave on the `spawn` stream. */
function rollArchetype(rng: RngService, wave: number): EnemyDef {
  let total = 0;
  for (const c of COMPOSITION) {
    if (wave >= c.fromWave) total += c.weight;
  }
  let roll = rng.get('spawn').next_range(0, total);
  for (const c of COMPOSITION) {
    if (wave < c.fromWave) continue;
    roll -= c.weight;
    if (roll < 0) return findEnemyDef(c.id);
  }
  return findEnemyDef(DRIFTFIN_ID);
}

/**
 * Spend banked threat on homogeneous squads. With `fixedDef` null (normal
 * assaults) each formation rolls its archetype from the wave-gated
 * composition table; the bonus game passes the void-fiend def so its
 * composition stays unchanged. From wave 4 each spawned unit also rolls the
 * elite chance on the `loot` stream (elite flag + hp × 3).
 */
function spendThreat(world: WorldState, state: DirectorState, tick: number, fixedDef: EnemyDef | null): void {
  const rng = world.rng;
  const events = world.events;
  const stepRate = cfg.getNumber('sim.step_rate_hz');
  const runTime = world.runTime;
  const safetyRadius = cfg.getNumber('spawn.safety_radius');
  const telegraphSeconds = cfg.getNumber('spawn.in_view_telegraph');
  const telegraphTicks = Math.ceil(telegraphSeconds * stepRate);
  const globalCap = Math.min(500, world.enemies.capacity);
  const eliteChance = eliteChanceForWave(state.wave.wave);

  // Spend threat on formations while budget and population allow.
  let safetyBreak = 0;
  while (state.threat > 0 && world.enemies.aliveCount < globalCap) {
    if (safetyBreak++ > 32) break; // guard against runaway loops in pathological cases

    // Homogeneous squad: one archetype per formation. The roll COMMITS: the
    // pending def waits for the budget to cover its squad instead of being
    // re-rolled every tick — otherwise cheap defs (dartling/driftfin) always
    // snatch the trickling threat and expensive squads never spawn.
    const def = fixedDef ?? (state.pendingDef ??= rollArchetype(rng, state.wave.wave));
    const popCap = Math.min(def.pop_cap, globalCap);
    if (world.enemies.aliveCount >= popCap) {
      // Population-capped: drop a rolled pending def so other archetypes can
      // still spawn; the bonus game's fixed def keeps the old hard stop.
      if (fixedDef) break;
      state.pendingDef = null;
      break;
    }

    const formationUnits = generateFormation(world, rng);
    const spawnCount = formationUnits.length;
    const cost = spawnCount * def.threat_score;
    if (state.threat < cost || world.enemies.aliveCount + spawnCount > popCap) break;

    // Determine if any spawn point is in camera view; if so, telegraph all units.
    let anyInView = false;
    for (const unit of formationUnits) {
      if (isInCameraView(unit.positions[0], world.player.pos)) {
        anyInView = true;
        break;
      }
    }
    // Every spawn gets at least the grace telegraph so enemies scale in through
    // their portal instead of popping in fully formed and instantly targetable.
    const telegraphUntil = anyInView
      ? tick + Math.max(telegraphTicks, SPAWN_GRACE_TICKS)
      : tick + SPAWN_GRACE_TICKS;

    for (const unit of formationUnits) {
      const pos = unit.positions[0];
      // Hard safety guard: never spawn within 12 u of the player.
      if (dist(pos, world.player.pos) < safetyRadius) continue;

      const e = world.enemies.acquire();
      if (!e) break;
      initEnemy(e, def, pos, unit.goal, runTime, tick, telegraphUntil);
      // Elite roll (spec-arsenal): champion units with triple hit points.
      if (eliteChance > 0 && rng.get('loot').next_range(0, 1) < eliteChance) {
        e.elite = true;
        e.hp *= 3;
        e.max_hp *= 3;
      }
      events.emit(
        'spawn',
        { kind: 'enemy', def_id: def.id, pos: { x: pos.x, y: pos.y }, telegraph_until: telegraphUntil },
        tick
      );
    }

    state.threat -= cost;
    if (!fixedDef) state.pendingDef = null;
  }
}
